import { useEffect, useState, type ReactNode } from "react";
import WebApp from "@twa-dev/sdk";
import { getColorScheme, getTheme, getUser, initTelegram } from "./telegram";
import Products from "./screens/Products";
import Diary from "./screens/Diary";
import { useKeyboardOpen } from "./hooks/useKeyboardOpen";
import { useIsFullscreen, useSafeAreaInsets } from "./hooks/useSafeArea";

type Tab = "diary" | "products";

const FALLBACK_USER_ID = 101210664;

export default function App() {
  const [user, setUser] = useState(getUser());
  const [theme, setTheme] = useState(getTheme());
  const [scheme, setScheme] = useState(getColorScheme());
  const [tab, setTab] = useState<Tab>("diary");
  const keyboardOpen = useKeyboardOpen();
  const insets = useSafeAreaInsets();
  const isFullscreen = useIsFullscreen();

  useEffect(() => {
    initTelegram();
    setUser(getUser());
    setTheme(getTheme());
    setScheme(getColorScheme());
    // На iOS Telegram CSS env(safe-area-inset-top) внутри iframe не покрывает
    // Dynamic Island — JS API даёт правильные значения только в fullscreen.
    try {
      const w = WebApp as unknown as { requestFullscreen?: () => void };
      if (typeof w.requestFullscreen === "function") w.requestFullscreen();
    } catch {
      // вне Telegram или старый клиент — игнор
    }
  }, []);

  const bg = theme.bg_color ?? (scheme === "dark" ? "#17212b" : "#ffffff");
  const fg = theme.text_color ?? (scheme === "dark" ? "#f5f5f5" : "#1a1a1a");

  const tabActive = theme.button_color ?? "#007AFF";
  const tabInactive = theme.hint_color ?? "#8E8E93";
  const tabBarBg = scheme === "dark" ? "rgba(28,28,30,0.55)" : "rgba(255,255,255,0.6)";
  const tabPillBg = scheme === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)";
  const tabBarShadow = scheme === "dark"
    ? "0 12px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.25)"
    : "0 12px 40px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.06)";

  const userId = user?.id ?? FALLBACK_USER_ID;
  const topOffset = insets.top;
  const leftOffset = insets.left;
  const rightOffset = insets.right;

  return (
    <div style={{
      minHeight: "100vh",
      width: "100%",
      maxWidth: "100%",
      overflowX: "hidden",
      background: bg,
      color: fg,
      fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
      paddingLeft: `max(${leftOffset}px, env(safe-area-inset-left, 0px), var(--tg-safe-area-inset-left, 0px))`,
      paddingRight: `max(${rightOffset}px, env(safe-area-inset-right, 0px), var(--tg-safe-area-inset-right, 0px))`,
      paddingBottom: `calc(92px + max(${insets.bottom}px, env(safe-area-inset-bottom), var(--tg-safe-area-inset-bottom, 0px)))`,
      boxSizing: "border-box",
    }}>
      {tab === "diary" && (
        <Diary
          theme={theme}
          scheme={scheme}
          userId={userId}
          topOffset={topOffset}
          leftOffset={leftOffset}
          rightOffset={rightOffset}
          isFullscreen={isFullscreen}
        />
      )}
      {tab === "products" && <Products theme={theme} scheme={scheme} topOffset={topOffset} />}

      <div
        style={{
          position: "fixed",
          left: `calc(14px + max(${leftOffset}px, env(safe-area-inset-left, 0px), var(--tg-safe-area-inset-left, 0px)))`,
          right: `calc(14px + max(${rightOffset}px, env(safe-area-inset-right, 0px), var(--tg-safe-area-inset-right, 0px)))`,
          bottom: `calc(16px + max(${insets.bottom}px, env(safe-area-inset-bottom), var(--tg-safe-area-inset-bottom, 0px)))`,
          zIndex: 10,
          display: keyboardOpen ? "none" : "flex",
          gap: 4,
          padding: 6,
          background: tabBarBg,
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
          borderRadius: 32,
          boxShadow: tabBarShadow,
        }}
      >
        <TabItem label="Дневник" active={tab === "diary"} activeColor={tabActive} inactiveColor={tabInactive} pillBg={tabPillBg} onClick={() => setTab("diary")}>
          <DiaryIcon active={tab === "diary"} />
        </TabItem>
        <TabItem label="Каталог" active={tab === "products"} activeColor={tabActive} inactiveColor={tabInactive} pillBg={tabPillBg} onClick={() => setTab("products")}>
          <CatalogIcon active={tab === "products"} />
        </TabItem>
      </div>
    </div>
  );
}

type TabItemProps = {
  label: string;
  active: boolean;
  activeColor: string;
  inactiveColor: string;
  pillBg: string;
  onClick: () => void;
  children: ReactNode;
};

function TabItem({ label, active, activeColor, inactiveColor, pillBg, onClick, children }: TabItemProps) {
  const color = active ? activeColor : inactiveColor;
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        height: 48,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        background: active ? pillBg : "transparent",
        border: "none",
        color,
        cursor: "pointer",
        padding: "8px 16px",
        borderRadius: 24,
        transition: "background 200ms ease, color 200ms ease",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <span style={{ display: "inline-flex", width: 22, height: 22, color }}>{children}</span>
      <span style={{ fontSize: 13, fontWeight: active ? 600 : 500, letterSpacing: 0.1, lineHeight: 1 }}>{label}</span>
    </button>
  );
}

type IconProps = { active: boolean };

function DiaryIcon({ active }: IconProps) {
  const sw = active ? 2.2 : 1.8;
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 4h12a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V4z" fill={active ? "currentColor" : "none"} />
      <path d="M5 4v14a2 2 0 0 0 2 2" stroke={active ? "rgba(255,255,255,0.85)" : "currentColor"} />
      <path d="M9 8h6" stroke={active ? "rgba(255,255,255,0.85)" : "currentColor"} />
      <path d="M9 12h6" stroke={active ? "rgba(255,255,255,0.85)" : "currentColor"} />
      <path d="M9 16h4" stroke={active ? "rgba(255,255,255,0.85)" : "currentColor"} />
    </svg>
  );
}

function CatalogIcon({ active }: IconProps) {
  const sw = active ? 2.2 : 1.8;
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="8" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="20" y2="12" />
      <line x1="8" y1="18" x2="20" y2="18" />
      <circle cx="4" cy="6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4" cy="18" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}
